import React, { useState, useEffect, useRef } from "react";

interface TextareaProps extends React.InputHTMLAttributes<HTMLTextAreaElement> {
  readonly id: string;
  readonly label?: string;
  readonly placeholder?: string;
  readonly error?: string;
  readonly value?: string;
  readonly onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  readonly onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  readonly onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  readonly onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  readonly name?: string;
  readonly className?: string;
  readonly icon?: React.ReactNode;
  readonly containerClassName?: string;
  readonly labelClassName?: string;
  readonly inputClassName?: string;
  readonly endIcon?: React.ReactNode;
  readonly inputRef?: React.RefObject<HTMLTextAreaElement>;
  readonly activeColor?: string;
  readonly innerContainerClassName?: string;
  readonly maxLength?: number;
  readonly disableAutoGrow?: boolean;
}

export default function Textarea({
  id,
  label,
  placeholder,
  error,
  value,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  name,
  icon,
  labelClassName,
  containerClassName,
  inputClassName,
  className,
  endIcon,
  inputRef,
  activeColor = "#253858",
  autoFocus = false,
  innerContainerClassName,
  maxLength,
  disableAutoGrow = false,
}: TextareaProps) {
  const [isFocused, setIsFocused] = useState(false);
  const localRef = useRef<HTMLTextAreaElement>(null);

  const clonedIcon =
    icon &&
    React.cloneElement(icon as React.ReactElement, {
      color: error ? "#EB5757" : isFocused ? activeColor : "#BDC0C3",
    });

  const autoGrow = () => {
    if (disableAutoGrow) return;
    const el = inputRef?.current || localRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    autoGrow();
    if (maxLength && e.target.value.length <= maxLength) {
      onChange?.(e);
    } else if (!maxLength) {
      onChange?.(e);
    }
  };

  useEffect(() => {
    if (!disableAutoGrow) autoGrow();
  }, [value, disableAutoGrow]);

  return (
    <div className={`relative ${containerClassName}`}>
      <div className={`flex flex-col gap-[6px] ${innerContainerClassName}`}>
        {label && (
          <label
            htmlFor={id}
            className={`text-xs leading-[18px] text-[#79808A] ${labelClassName}`}
          >
            {label}
          </label>
        )}
        <div
          className={`flex gap-[8px] flex-row border rounded-[4px] py-[16px] px-[10px] ${
            disableAutoGrow ? "items-start" : "items-center"
          } ${inputClassName} ${
            isFocused ? "!border-[#79808A]" : "border-[#E9ECF2]"
          } ${error && "border-danger"}`}
        >
          {icon && <div>{clonedIcon}</div>}
          <textarea
            id={id}
            className={`text-sm rounded w-full text-gray-700 leading-[21px] focus:outline-none placeholder:text-[#BDC0C3] placeholder:font-normal placeholder:text-sm placeholder:leading-[21px] resize-none ${className}`}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onBlur={(e) => {
              setIsFocused(false);
              onBlur?.(e);
            }}
            onFocus={(e) => {
              setIsFocused(true);
              autoGrow(); // Optional: grow on focus
              onFocus?.(e);
            }}
            onKeyDown={onKeyDown}
            name={name}
            ref={inputRef || localRef}
            autoFocus={autoFocus}
            rows={1} // ensures it starts small
          />
          {endIcon && <div>{endIcon}</div>}
        </div>
      </div>
      {error && (
        <p className="text-[11px] leading-[18px] text-danger">{error}</p>
      )}
      {value && maxLength && (
        <p className="flex justify-end text-[11px] leading-[18px] text-gray-500 text-right">
          {value.length}/{maxLength} characters
        </p>
      )}
    </div>
  );
}
